import { useState, useEffect, useMemo, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

/* ---------------------------
   Types
   --------------------------- */
export type PricePoint = { date: string; close: number; valeur_echange?: number };
export type DividendPoint = { date: string; amount: number };

export type SymbolFull = {
  symbole: string;
  nom_societe?: string;
  secteur?: string;
  history?: PricePoint[];
  dividends?: DividendPoint[];
  last?: { close: number; date: string; ytd?: number };
};

export interface StockPerformance {
  symbole: string;
  nom_societe: string;
  variation_annee_pct: number;
  cours_cloture: number;
}

export interface DividendYield {
  symbole: string;
  nom_societe: string;
  montant_dividende: number;
  cours_cloture: number;
  rendement_pct: number;
}

export interface DividendHistory {
  symbole: string;
  nom_societe: string;
  annees_consecutives: number;
  dernier_montant: number;
}

// Cached stock data from API
interface CachedStock {
  symbole: string;
  nom_societe?: string;
  secteur?: string;
  cours_cloture?: number;
  variation_jour_pct?: number;
  ytd_pct?: number;
  sma_20?: number;
  sma_50?: number;
  sma_100?: number;
  rsi_14?: number;
  volatilite_30j?: number;
  div_yield_pct?: number;
  swing_score?: number;
  carnet_pression?: number;
  liquidity_score?: number;
  liquidity_grade?: string;
  div_regularity_score?: number;
  // Dual liquidity scores
  liq_long_terme_score?: number;
  liq_long_terme_grade?: string;
  liq_court_terme_score?: number;
  liq_court_terme_grade?: string;
  liq_ct_is_blocked?: boolean;
  // Technical Indicators
  bb_upper?: number;
  bb_lower?: number;
  bb_signal?: string;
  cci?: number;
  obv?: number;
  obv_trend?: string;
  stoch_k?: number;
  stoch_d?: number;
  variation_5j_pct?: number;
  momentum_6m_pct?: number;
  avg_volume_20j?: number;
  avg_turnover_20j?: number;
  support_20j?: number;
  resistance_20j?: number;
  // Optional pre-computed score (if provided by backend)
  tech_score?: number;
}

interface CachedMarketSummary {
  market_mood?: number;
  market_sentiment?: string;
  pct_up?: number;
  pct_above_ma20?: number;
  mean_ytd?: number;
  median_ytd?: number;
  avg_volatility?: number;
  stocks_analyzed?: number;
  stocks_bullish?: number;
  stocks_bearish?: number;
  seasonality_data?: { month: number; avgChange: number; winRate: number }[];
}

interface CachedGem {
  symbole: string;
  nom: string;
  yield?: number;
  ytd?: number;
  rsi?: number;
  vol?: number;
  regularity?: number;
  liquidity?: number;
}

interface CachedPerformer {
  symbole: string;
  nom?: string;
  nom_societe?: string;
  ytd?: number;
  variation_annee_pct?: number;
  cours?: number;
  cours_cloture?: number;
  div?: number;
  yield?: number;
  rendement_pct?: number;
}

interface CachedAnalysisResponse {
  date: string;
  error?: string;
  market_summary?: CachedMarketSummary;
  gems?: {
    yield_kings: CachedGem[];
    momentum_stars: CachedGem[];
    safe_havens: CachedGem[];
  };
  top_performers?: CachedPerformer[];
  worst_performers?: CachedPerformer[];
  top_yields?: CachedPerformer[];
  sector_heatmap?: { sector: string; avgYtd: number; avgYield: number; avgVol: number; count: number }[];
  stocks: CachedStock[];
}

// Row type exposed to consumers
export type AnalysisRow = {
  symbole: string;
  nom?: string;
  secteur?: string;
  cours_cloture?: number;
  ytd?: number;
  vol30?: number;
  mm20_last?: number;
  mm50_last?: number;
  rsi_last?: number;
  div_yield?: number;
  div_regularity_score?: number;
  avgTurnover?: number;
  liquidity_score?: number;
  liquidity_grade?: string;
  // Dual liquidity
  liq_long_terme_score?: number;
  liq_long_terme_grade?: string;
  liq_court_terme_score?: number;
  liq_court_terme_grade?: string;
  liq_ct_is_blocked?: boolean;
  // Technical indicators
  bb_upper?: number;
  bb_lower?: number;
  bb_signal?: string;
  cci?: number;
  obv?: number;
  obv_trend?: string;
  stoch_k?: number;
  stoch_d?: number;
  avgVolume?: number;
  support?: number;
  resistance?: number;
  variation_5j_pct?: number;
  momentum_6m_pct?: number;
  // Computed
  tech_score: number;
};

/** Calculate tech score from row indicators */
function computeTechScore(r: Omit<AnalysisRow, 'tech_score'>): number {
  let score = 50;

  // 1. RSI (±15)
  if (r.rsi_last) {
    if (r.rsi_last < 30) score += 15;
    else if (r.rsi_last < 40) score += 5;
    else if (r.rsi_last > 70) score -= 15;
    else if (r.rsi_last > 60) score -= 5;
  }

  // 2. Trend SMA20/SMA50 (±30)
  if (r.mm20_last && r.mm50_last && r.cours_cloture) {
    if (r.cours_cloture > r.mm20_last && r.mm20_last > r.mm50_last) score += 30;
    else if (r.cours_cloture > r.mm20_last) score += 15;
    else if (r.cours_cloture < r.mm20_last && r.mm20_last < r.mm50_last) score -= 30;
    else if (r.cours_cloture < r.mm20_last) score -= 15;
  }

  // 3. Momentum 6m or YTD fallback (±20)
  if (r.momentum_6m_pct !== undefined) {
    if (r.momentum_6m_pct > 15) score += 20;
    else if (r.momentum_6m_pct > 5) score += 10;
    else if (r.momentum_6m_pct < -15) score -= 20;
    else if (r.momentum_6m_pct < -5) score -= 10;
  } else if (r.ytd !== undefined) {
    if (r.ytd > 20) score += 10;
    else if (r.ytd > 5) score += 5;
    else if (r.ytd < -20) score -= 10;
    else if (r.ytd < -5) score -= 5;
  }

  return Math.max(0, Math.min(100, score));
}

export function useGlobalAnalysis() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cached data from API
  const [cachedData, setCachedData] = useState<CachedAnalysisResponse | null>(null);

  // Legacy states (for compatibility)
  const [topPerformers, setTopPerformers] = useState<StockPerformance[]>([]);
  const [topYields, setTopYields] = useState<DividendYield[]>([]);
  const [consistentDividends] = useState<DividendHistory[]>([]);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Try cached data first
      let data: CachedAnalysisResponse | null = null;
      try {
        const cachedRes = await fetch(`${API_URL}/v1/analysis/cached`, { headers });
        if (cachedRes.ok) {
          data = await cachedRes.json();
        }
      } catch (e) {
        console.warn('Cache not available, will fallback to active stocks');
      }

      // If cache is empty or has error, fallback to basic stock data
      if (!data || data.error || !data.stocks || data.stocks.length === 0) {
        console.log('Fallback: using /v1/stocks/active');
        const activeRes = await fetch(`${API_URL}/v1/stocks/active`, { headers });
        if (!activeRes.ok) {
          throw new Error('Could not fetch stock data');
        }
        const activeStocks = await activeRes.json();

        data = {
          date: new Date().toISOString().slice(0, 10),
          stocks: activeStocks.map((s: CachedStock) => ({
            symbole: s.symbole,
            nom_societe: s.nom_societe,
            secteur: s.secteur,
            cours_cloture: null,
            ytd_pct: 0,
            rsi_14: null,
            sma_20: null,
            sma_50: null,
            volatilite_30j: null,
            div_yield_pct: null
          })),
          market_summary: {
            mean_ytd: 0,
            pct_up: 50,
            avg_volatility: 15
          },
          gems: { yield_kings: [], momentum_stars: [], safe_havens: [] }
        };
      }

      setCachedData(data);

      // Map top performers from cache
      if (data.top_performers) {
        setTopPerformers(data.top_performers.map((p) => ({
          symbole: p.symbole,
          nom_societe: p.nom || p.nom_societe || '',
          variation_annee_pct: p.ytd ?? p.variation_annee_pct ?? 0,
          cours_cloture: p.cours ?? p.cours_cloture ?? 0
        })));
      }

      if (data.top_yields) {
        setTopYields(data.top_yields.map((y) => ({
          symbole: y.symbole,
          nom_societe: y.nom || y.nom_societe || '',
          montant_dividende: y.div || 0,
          cours_cloture: y.cours || 0,
          rendement_pct: y.yield || y.rendement_pct || 0
        })));
      }

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement des données';
      console.error('Analysis error:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      runAnalysis();
    }
  }, [token, runAnalysis]);

  // Build analysis from cached data (fast — no computation)
  const analysis = useMemo(() => {
    if (!cachedData || !cachedData.stocks || cachedData.stocks.length === 0) {
      return null;
    }

    const stocks = cachedData.stocks;
    const summary = cachedData.market_summary;

    // Build rows from cached stocks with tech_score
    const rows: AnalysisRow[] = stocks.map(s => {
      const base = {
        symbole: s.symbole,
        nom: s.nom_societe,
        secteur: s.secteur ? s.secteur.split('/')[0].trim() : 'Autre',
        cours_cloture: s.cours_cloture,
        ytd: s.ytd_pct || 0,
        vol30: s.volatilite_30j ? s.volatilite_30j / 100 : undefined,
        mm20_last: s.sma_20,
        mm50_last: s.sma_50,
        rsi_last: s.rsi_14,
        div_yield: s.div_yield_pct,
        div_regularity_score: s.div_regularity_score || 0,
        avgTurnover: s.avg_turnover_20j,
        liquidity_score: s.liquidity_score,
        liquidity_grade: s.liquidity_grade,
        // Dual liquidity
        liq_long_terme_score: s.liq_long_terme_score,
        liq_long_terme_grade: s.liq_long_terme_grade,
        liq_court_terme_score: s.liq_court_terme_score,
        liq_court_terme_grade: s.liq_court_terme_grade,
        liq_ct_is_blocked: s.liq_ct_is_blocked,
        // Technical indicators
        bb_upper: s.bb_upper,
        bb_lower: s.bb_lower,
        bb_signal: s.bb_signal,
        cci: s.cci,
        obv: s.obv,
        obv_trend: s.obv_trend,
        stoch_k: s.stoch_k,
        stoch_d: s.stoch_d,
        avgVolume: s.avg_volume_20j,
        support: s.support_20j,
        resistance: s.resistance_20j,
        variation_5j_pct: s.variation_5j_pct,
        momentum_6m_pct: s.momentum_6m_pct,
      };
      return { ...base, tech_score: s.tech_score ?? computeTechScore(base) };
    });

    const available = rows.filter(r => r.cours_cloture !== undefined);

    // Use pre-calculated market summary
    const meanYtd = summary?.mean_ytd ?? 0;
    const medianYtd = summary?.median_ytd ?? 0;
    const pctUp = summary?.pct_up ?? 50;
    const avgVol30 = summary?.avg_volatility ? summary.avg_volatility / 100 : 0.15;
    // Pas de repli à 50 : un sentiment « neutre » inventé serait affiché comme une mesure.
    const marketMood = summary?.market_mood ?? null;

    // Sector data from rows
    const bySector: Record<string, { count: number; sumYtd: number; sumYield: number; sumVol: number }> = {};
    rows.forEach(r => {
      const s = r.secteur ?? 'Autre';
      if (!bySector[s]) bySector[s] = { count: 0, sumYtd: 0, sumYield: 0, sumVol: 0 };
      bySector[s].count += 1;
      bySector[s].sumYtd += r.ytd ?? 0;
      bySector[s].sumYield += r.div_yield ?? 0;
      bySector[s].sumVol += (r.vol30 ?? 0);
    });
    const sectorData = Object.entries(bySector)
      .map(([k, v]) => ({
        sector: k,
        avgYtd: v.sumYtd / Math.max(1, v.count),
        avgYield: v.sumYield / Math.max(1, v.count),
        avgVol: v.sumVol / Math.max(1, v.count),
        count: v.count,
      }))
      .sort((a, b) => b.avgYtd - a.avgYtd);

    // Top volatile from rows
    const topVolatile = [...available]
      .filter(r => r.vol30 !== undefined)
      .sort((a, b) => (b.vol30 ?? 0) - (a.vol30 ?? 0))
      .slice(0, 10);

    // Seasonality — use real data, empty array if none
    const seasonality = summary?.seasonality_data && summary.seasonality_data.length > 0
      ? summary.seasonality_data
      : [];

    // Gems from cache
    const cachedGems = cachedData.gems || { yield_kings: [], momentum_stars: [], safe_havens: [] };

    const gems = {
      yieldKings: cachedGems.yield_kings.slice(0, 3).map(g => {
        const match = rows.find(r => r.symbole === g.symbole);
        return match || { symbole: g.symbole, nom: g.nom, div_yield: g.yield, tech_score: 50 } as AnalysisRow;
      }),
      momentumStars: cachedGems.momentum_stars.slice(0, 3).map(g => {
        const match = rows.find(r => r.symbole === g.symbole);
        return match || { symbole: g.symbole, nom: g.nom, ytd: g.ytd, rsi_last: g.rsi, tech_score: 50 } as AnalysisRow;
      }),
      safeHavens: cachedGems.safe_havens.slice(0, 3).map(g => {
        const match = rows.find(r => r.symbole === g.symbole);
        return match || { symbole: g.symbole, nom: g.nom, vol30: (g.vol || 0) / 100, div_yield: g.yield, tech_score: 50 } as AnalysisRow;
      })
    };

    return {
      rows,
      available,
      meanYtd,
      medianYtd,
      pctUp,
      avgVol30,
      sectorData,
      worstPerformers: cachedData.worst_performers || [],
      topVolatile,
      seasonality,
      marketMood,
      gems,
    };
  }, [cachedData]);

  // CSV Export — all displayed columns
  function exportCSVRows() {
    if (!analysis) return;
    const expRows = analysis.rows.slice(0, 200);
    const headers = [
      'symbole', 'nom', 'secteur', 'cours', 'ytd_pct', 'var_5j_pct',
      'vol30_pct', 'rsi_14', 'tech_score',
      'bb_signal', 'cci', 'stoch_k', 'stoch_d',
      'div_yield_pct', 'div_regularity',
      'liq_lt_grade', 'liq_lt_score', 'liq_ct_grade', 'liq_ct_score',
      'liquidity_grade', 'liquidity_score'
    ];
    const csv =
      headers.join(',') +
      '\n' +
      expRows
        .map(
          (r) =>
            [
              r.symbole,
              `"${(r.nom || '').replace(/"/g, '""')}"`,
              `"${(r.secteur || '').replace(/"/g, '""')}"`,
              r.cours_cloture ?? '',
              typeof r.ytd === 'number' ? r.ytd.toFixed(2) : '',
              r.variation_5j_pct != null ? r.variation_5j_pct.toFixed(2) : '',
              r.vol30 ? (r.vol30 * 100).toFixed(2) : '',
              r.rsi_last ? r.rsi_last.toFixed(1) : '',
              r.tech_score,
              r.bb_signal ?? '',
              r.cci != null ? r.cci.toFixed(1) : '',
              r.stoch_k != null ? r.stoch_k.toFixed(1) : '',
              r.stoch_d != null ? r.stoch_d.toFixed(1) : '',
              r.div_yield != null ? r.div_yield.toFixed(2) : '',
              r.div_regularity_score ?? '',
              r.liq_long_terme_grade ?? '',
              r.liq_long_terme_score ?? '',
              r.liq_court_terme_grade ?? '',
              r.liq_court_terme_score ?? '',
              r.liquidity_grade ?? '',
              r.liquidity_score ?? '',
            ].join(',')
        )
        .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `market_scan_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return {
    loading,
    error,
    setError,
    runAnalysis,
    analysis,
    exportCSVRows,
    topPerformers,
    topYields,
    consistentDividends,
  };
}
